/* eslint-disable */
import { useState, useEffect, ChangeEvent, useContext } from "react";

import { LocationData } from "../models/locationData";
import { fetchWrapper } from "../common/helpers/fetch-wrapper";
import SelectInput from "./common/SelectInput";
import { environment } from "../environments/environment";
import { LocalizationContext } from "../App";
import { BuildingHouseIcon } from "../common/icons";

export interface AddressFormProps {
  inputAddressData: LocationData;
  isPreviewMode: boolean;
  onChange: (updatedData: LocationData) => void;
}

export const AddressForm = ({ isPreviewMode, inputAddressData, onChange }: AddressFormProps) => {
  const [formValues, setFormValues] = useState<LocationData>(inputAddressData);
  const [citiesDropdown, setCitiesDropdown] = useState<[number, string][]>([]);
  const [selectedCity, setSelectedCity] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [selectedInputData, setSelectedInputData] = useState({});
  const l10n = useContext(LocalizationContext);

  useEffect(() => {
    const fetchAndSetCityData = async () => {
      if (isPreviewMode && formValues?.zipCode) {
        await fetchCityNameByZipCode(formValues?.zipCode);
      } else {
        setFormValues(inputAddressData);
        if (inputAddressData?.zipCode.length > 0) {
          const cities = await getCityDropDown(inputAddressData.zipCode);
          setCitiesDropdown(cities);
          if (cities.length === 1) {
            setFormValues((prevState: any) => ({
              ...prevState,
              cityId: cities[0][0],
              cityName: cities[0][1],
            }));
            setSelectedCity({ value: cities[0][0], label: cities[0][1] });
          } else if (cities.length > 1) {
            let geoObject =
              cities.find((obj: any) => {
                return obj[0] === formValues?.cityId;
              }) ?? [];
            setFormValues((prevState: any) => ({
              ...prevState,
              cityId: inputAddressData.cityId ?? undefined,
              cityName: geoObject[1] ?? "",
            }));
            setSelectedCity({
              value: inputAddressData.cityId as number,
              label: geoObject[1],
            });
          }
        }
        onChange(inputAddressData);
      }
    };

    fetchAndSetCityData();
  }, [inputAddressData, isPreviewMode]);

  const fetchCityNameByZipCode = async (zipCode: string) => {
    try {
      const response = await fetchWrapper.get(
        `${environment.api.geoLocationService}city/${zipCode}/en`
      );
      if (response.data && response.data.length > 0) {
        if (response.data.length > 1) {
          let geoObject = response.data.find((obj: any) => {
            return obj.id === formValues?.cityId;
          });
          setFormValues((prevState) => ({
            ...prevState,
            cityName: geoObject.name,
          }));
        } else {
          setFormValues((prevState) => ({
            ...prevState,
            cityName: response.data[0].name,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching city name:", error);
    }
  };

  const handleSelectedInputChange = (updatedData: any) => {
    setSelectedInputData(updatedData);
    let updatedFormValues = { ...formValues } as LocationData;

    updatedFormValues = {
      ...updatedFormValues,
      cityId: updatedData.value,
      cityName: updatedData.label,
    };

    onChange(updatedFormValues);
  };

  const getCityDropDown = async (zipCode: string) => {
    try {
      const response = await fetchWrapper.get(
        `${environment.api.geoLocationService}city/${zipCode}/en`
      );
      const data = response.data || [];
      const cities = data.map((city: any) => [city.id, city.name]);
      return cities;
    } catch (error) {
      console.error("Error adjusting dropdowns:", error);
    }
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let updatedFormValues = { ...formValues, [name]: value } as LocationData;
    setFormValues(updatedFormValues);

    if (name === "zipCode" && value.length === 4) {
      const cities = await getCityDropDown(value);
      setCitiesDropdown(cities);

      if (cities.length === 1) {
        updatedFormValues = {
          ...updatedFormValues,
          cityId: cities[0][0],
          cityName: cities[0][1],
        };
        setSelectedCity({ value: cities[0][0], label: cities[0][1] });
      } else if (cities.length > 1) {
        updatedFormValues = {
          ...updatedFormValues,
          cityId: undefined,
          cityName: "",
        };
        setSelectedCity(null);
      }
    }
    onChange(updatedFormValues);
  };

  const options = citiesDropdown.map(([id, name]) => ({
    value: id,
    label: name,
  }));

  return (
    <>
      {isPreviewMode ? (
        <div className="w-full flex">
          <div className="flex flex-1 gap-4 items-center">
            <BuildingHouseIcon className="w-5 h-5 stroke-0 stroke-card fill-card" />
            <div className="flex flex-col">
              <span className="text-card">
                {formValues?.address1} {formValues?.address2}
              </span>
              <span className="text-card">
                {formValues?.zipCode} {formValues?.cityName}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="address1">{l10n.label.address1}</label>
            <input
              className="form-control"
              id="address1"
              name="address1"
              onChange={handleChange}
              type="text"
              value={formValues?.address1 || ""}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address2">{l10n.label.address2}</label>
            <input
              className="form-control"
              id="address2"
              name="address2"
              onChange={handleChange}
              type="text"
              value={formValues?.address2 || ""}
            />
          </div>

          <div className="form-group">
            <label htmlFor="zipCode">{l10n.label.postalCode}</label>
            <input
              className="form-control"
              id="zipCode"
              name="zipCode"
              onChange={handleChange}
              type="text"
              value={formValues?.zipCode || ""}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cityName">{l10n.label.city}</label>
            <SelectInput
              id="cityName"
              onChange={handleSelectedInputChange}
              options={options}
              preSelectedOption={selectedCity}
            />
          </div>
        </>
      )}
    </>
  );
};
